import type { TBrightnessAutoConfig } from '@lucimark/shared/models/player-brightness'
import {
  brightnessAutoConfigSchema,
  createBrightnessSmoother,
  localMinutesOfDay,
  resolveScheduledBrightnessPercent,
  screenBrightnessPercentSchema
} from '@lucimark/shared/models/player-brightness'
import z from 'zod'

export const BRIGHTNESS_CONFIG_STORAGE_KEY = 'lucimark.brightnessConfig.v1'
/** Schedule boundaries are minute-granular, so a 30 s tick applies a change within half a minute. */
export const BRIGHTNESS_SCHEDULE_TICK_MS = 30_000
/** Sensor sampling cadence while a sensor mode is active. */
export const BRIGHTNESS_SENSOR_TICK_MS = 5_000

export interface IBrightnessConfig {
  /** Fixed target and the fallback for automatic modes; null releases control. */
  percent: number | null
  auto: TBrightnessAutoConfig | null
}

const storedConfigSchema = z.object({
  percent: screenBrightnessPercentSchema.nullable(),
  auto: brightnessAutoConfigSchema.nullable()
})

export interface IBrightnessAutomationDeps {
  /** Applies a target to the device (`null` releases). Resolves false when it could not be applied. */
  apply: (percent: number | null) => Promise<boolean>
  /** Hands brightness to Android's adaptive mode. Resolves false when the device cannot (keep the fallback). */
  applyAutomatic?: () => Promise<boolean>
  /** Called after a schedule/config change was applied, so Studio sees the new level promptly. */
  onApplied?: () => void
  startSensor: () => Promise<boolean>
  stopSensor: () => Promise<void>
  /** Latest lux, or null while there is no reading. */
  readLux: () => Promise<number | null>
  now?: () => Date
  storage?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null
}

/**
 * Runs the operator's brightness policy on the device so it keeps working offline:
 * fixed → apply once; schedule → re-evaluate every tick; sensor → smoothed ambient-light following.
 * The fixed `percent` is always the fallback (outside every slot, no sensor / no reading yet).
 */
export function createBrightnessAutomation(deps: IBrightnessAutomationDeps) {
  const now = deps.now ?? (() => new Date())
  let timer: ReturnType<typeof setInterval> | null = null
  let sensorRunning = false
  let lastApplied: number | null | undefined
  let generation = 0
  const smoother = createBrightnessSmoother()
  function persist(config: IBrightnessConfig): void {
    try {
      deps.storage?.setItem(BRIGHTNESS_CONFIG_STORAGE_KEY, JSON.stringify(config))
    }
    catch {
      // storage is best-effort; the server re-sends the config on every connect
    }
  }

  async function applyIfChanged(percent: number | null, notify: boolean): Promise<void> {
    if (percent === lastApplied) {
      return
    }
    const applied = await deps.apply(percent)
    if (applied) {
      lastApplied = percent
      if (notify) {
        deps.onApplied?.()
      }
    }
  }

  async function halt(): Promise<void> {
    generation += 1
    if (timer != null) {
      clearInterval(timer)
      timer = null
    }
    if (sensorRunning) {
      sensorRunning = false
      await deps.stopSensor()
    }
    smoother.reset()
  }

  async function setConfig(config: IBrightnessConfig, options: { persist?: boolean } = {}): Promise<void> {
    await halt()
    const myGeneration = generation
    if (options.persist !== false) {
      persist(config)
    }
    // A fresh config always re-applies, even if the number is unchanged (the OS may have drifted).
    lastApplied = undefined
    const { percent, auto } = config
    if (percent === null) {
      await applyIfChanged(null, true)
      return
    }
    if (auto == null) {
      await applyIfChanged(percent, true)
      return
    }
    if (auto.mode === 'system') {
      if (deps.applyAutomatic != null && await deps.applyAutomatic()) {
        deps.onApplied?.()
        return
      }
      // Android cannot take over (no Device Owner / WRITE_SETTINGS, or an older APK): keep the fixed fallback.
      await applyIfChanged(percent, true)
      return
    }
    if (auto.mode === 'schedule') {
      const tick = async (notify: boolean) => {
        const target = resolveScheduledBrightnessPercent(auto.slots, percent, localMinutesOfDay(now()))
        await applyIfChanged(target, notify)
      }
      await tick(true)
      if (myGeneration === generation) {
        timer = setInterval(() => void tick(true), BRIGHTNESS_SCHEDULE_TICK_MS)
      }
      return
    }
    // sensor mode: start from the fallback so the screen is sane until the first reading
    await applyIfChanged(percent, true)
    const started = await deps.startSensor()
    if (myGeneration !== generation) {
      if (started) {
        await deps.stopSensor()
      }
      return
    }
    if (!started) {
      return // no sensor: the fixed fallback stays in force
    }
    sensorRunning = true
    timer = setInterval(() => {
      void (async () => {
        const lux = await deps.readLux()
        if (lux == null || myGeneration !== generation) {
          return
        }
        const target = smoother.feed(lux, auto)
        if (target != null) {
          await applyIfChanged(target, false)
        }
      })()
    }, BRIGHTNESS_SENSOR_TICK_MS)
  }

  /** Re-applies the last stored config (boot / offline start), before the server has said anything. */
  async function restore(): Promise<void> {
    let raw: string | null = null
    try {
      raw = deps.storage?.getItem(BRIGHTNESS_CONFIG_STORAGE_KEY) ?? null
    }
    catch {
      raw = null
    }
    if (raw != null) {
      try {
        const parsed = storedConfigSchema.safeParse(JSON.parse(raw))
        if (parsed.success) {
          await setConfig(parsed.data, { persist: false })
        }
      }
      catch {
        // corrupt entry: ignore, the server config will replace it
      }
    }
  }

  return { setConfig, restore, stop: halt }
}
